import { Tree, type MoveHandler, type RenameHandler, type CreateHandler, type DeleteHandler } from 'react-arborist'
import { router } from '@inertiajs/react'
import type { SidebarTreeNode } from '@/types'
import WikiTreeNode from './WikiTreeNode'
import { Plus } from 'lucide-react'
import { useRef, useCallback, useEffect, useState } from 'react'

const OPEN_IDS_KEY = 'wiki-tree-open-ids'

function loadOpenIds(): Set<string> {
  try {
    const raw = sessionStorage.getItem(OPEN_IDS_KEY)
    return raw ? new Set(JSON.parse(raw)) : new Set()
  } catch {
    return new Set()
  }
}

function saveOpenIds(ids: Set<string>) {
  sessionStorage.setItem(OPEN_IDS_KEY, JSON.stringify([...ids]))
}

interface Props {
  tree: SidebarTreeNode[]
  currentSlug?: string
}

// react-arborist needs `name` field for rename, and `children` as array.
// Always include children (even empty []) so all nodes are droppable folders.
function transformTree(nodes: SidebarTreeNode[]): TreeData[] {
  return nodes.map(node => ({
    id: node.id,
    name: node.title,
    slug: node.slug,
    children: transformTree(node.children),
  }))
}

export interface TreeData {
  id: string
  name: string
  slug: string
  children: TreeData[]
}

export default function WikiTree({ tree, currentSlug }: Props) {
  const treeRef = useRef<any>(null)
  const [openIds, setOpenIds] = useState<Set<string>>(loadOpenIds)

  const data = transformTree(tree)
  // Key forces react-arborist to remount when tree structure changes (e.g., after creating/deleting a page)
  const treeKey = countNodes(tree)

  // Auto-expand ancestors of the current page
  useEffect(() => {
    if (!currentSlug) return
    const ancestors = findAncestorIds(data, currentSlug)
    if (ancestors.length > 0) {
      setOpenIds(prev => {
        const next = new Set(prev)
        ancestors.forEach(id => next.add(id))
        saveOpenIds(next)
        return next
      })
    }
  }, [currentSlug])

  const onMove: MoveHandler<TreeData> = useCallback(async ({ dragIds, parentId, index }) => {
    const dragId = dragIds[0]
    const node = findNodeById(data, dragId)
    if (!node) return

    try {
      const response = await fetch(`/notes/${node.slug}/move`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
        },
        body: JSON.stringify({ parent_id: parentId, position: index }),
      })

      if (!response.ok) throw new Error('Move failed')
      router.reload()
    } catch {
      router.reload()
    }
  }, [data])

  const onRename: RenameHandler<TreeData> = useCallback(async ({ id, name, node }) => {
    try {
      router.patch(`/notes/${node.data.slug}`, {
        note: { title: name },
      } as any, { preserveState: true })
    } catch {
      router.reload()
    }
  }, [])

  const onCreate: CreateHandler<TreeData> = useCallback(async ({ parentId, parentNode }) => {
    if (parentNode) {
      router.post(`/notes/${parentNode.data.slug}/children`, {
        note: { title: 'Untitled' },
      })
    } else {
      router.visit('/notes/new')
    }
    return null
  }, [])

  const onDelete: DeleteHandler<TreeData> = useCallback(async ({ nodes }) => {
    const node = nodes[0]
    if (!node) return
    if (!confirm(`Delete "${node.data.name}"?`)) return

    router.delete(`/notes/${node.data.slug}`, { preserveState: false })
  }, [])

  const handleCreateRoot = () => {
    router.visit('/notes/new')
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-2 mb-1">
        <span className="text-[11px] font-medium uppercase tracking-wider text-gray-400">Pages</span>
        <button
          onClick={handleCreateRoot}
          className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          title="New page"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        <Tree<TreeData>
          key={treeKey}
          ref={treeRef}
          data={data}
          width="100%"
          indent={16}
          rowHeight={28}
          openByDefault={false}
          initialOpenState={Object.fromEntries([...openIds].map(id => [id, true]))}
          selection={currentSlug ? findNodeIdBySlug(data, currentSlug) ?? undefined : undefined}
          onMove={onMove}
          onRename={onRename}
          onCreate={onCreate}
          onDelete={onDelete}
          onToggle={(id) => {
            setOpenIds(prev => {
              const next = new Set(prev)
              if (next.has(id)) next.delete(id)
              else next.add(id)
              saveOpenIds(next)
              return next
            })
          }}
        >
          {WikiTreeNode}
        </Tree>
      </div>
    </div>
  )
}

function countNodes(nodes: SidebarTreeNode[]): number {
  return nodes.reduce((sum, n) => sum + 1 + countNodes(n.children), 0)
}

function findNodeById(nodes: TreeData[], id: string): TreeData | null {
  for (const node of nodes) {
    if (node.id === id) return node
    if (node.children) {
      const found = findNodeById(node.children, id)
      if (found) return found
    }
  }
  return null
}

function findAncestorIds(nodes: TreeData[], slug: string, path: string[] = []): string[] {
  for (const node of nodes) {
    if (node.slug === slug) return path
    if (node.children.length > 0) {
      const found = findAncestorIds(node.children, slug, [...path, node.id])
      if (found.length > 0) return found
    }
  }
  return []
}

function findNodeIdBySlug(nodes: TreeData[], slug: string): string | null {
  for (const node of nodes) {
    if (node.slug === slug) return node.id
    if (node.children) {
      const found = findNodeIdBySlug(node.children, slug)
      if (found) return found
    }
  }
  return null
}
